/**
 * POCKETS — what the person in front of you is carrying, and your odds on each
 * of it.
 *
 * Non-modal, exactly like the LOCK window: the world keeps running behind it,
 * guards keep walking, and WASD drops the attempt and moves you. Standing with
 * your hand in someone's pocket while the room carries on is the whole tension,
 * and a modal dialog would throw it away.
 *
 * Shows the ODDS per item, because the decision the player is making is
 * "which of these is worth the risk" — and a percentage is the only honest way
 * to put that question. DOM only, no PixiJS.
 */

import type { Item } from '../sim/inventory';
import type { GameWindow } from './window';

export interface PocketRow {
  item: Item;
  /** 0..1 chance of lifting it cleanly right now. */
  chance: number;
}

export class PocketWindow {
  private readonly host: HTMLElement;
  private lastSig = '';

  constructor(private readonly window: GameWindow) {
    this.host = document.createElement('div');
    this.host.className = 'pocket-list';
    window.body.appendChild(this.host);
    window.root.style.display = 'none';
  }

  isOpen(): boolean {
    return this.window.root.style.display !== 'none';
  }

  hide(): void {
    if (this.isOpen()) {
      this.window.root.style.display = 'none';
      this.lastSig = '';
    }
  }

  /** Render the mark's pockets with `selected` highlighted. */
  show(name: string, rows: readonly PocketRow[], selected: number): void {
    this.window.root.style.display = '';
    const sig = `${name}|${selected}|${rows.map((r) => `${r.item.id}:${r.chance.toFixed(2)}`).join(',')}`;
    if (sig === this.lastSig) return;
    this.lastSig = sig;
    if (rows.length === 0) {
      this.host.innerHTML = `<div class="pocket-empty">${escapeHtml(name)} has nothing on them.</div>`;
      return;
    }
    this.host.innerHTML =
      `<div class="pocket-who">${escapeHtml(name)}</div>`
      + rows.map((r, i) => {
        const pct = Math.round(r.chance * 100);
        const risk = pct >= 60 ? 'good' : pct >= 35 ? 'fair' : 'bad';
        return `<div class="pocket-row${i === selected ? ' pocket-row--sel' : ''}">`
          + `<span class="pocket-glyph" style="color:#${r.item.color.toString(16).padStart(6, '0')}">${escapeHtml(r.item.glyph)}</span>`
          + `<span class="pocket-label">${escapeHtml(r.item.label)}</span>`
          + `<span class="pocket-odds pocket-odds--${risk}">${pct}%</span></div>`;
      }).join('')
      + '<div class="pocket-hint">PgUp/PgDn choose · E lift · move to walk away</div>';
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}
